// ResponsiveScheduleForm.js
import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  SwapVert as SwapVertIcon,
  DragIndicator as DragIndicatorIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import TimePickerInput from "./TimePickerInput";

function ResponsiveScheduleForm({ travelData, setTravelData }) {
  const [reorderingDates, setReorderingDates] = useState({});
  const dragDateIndex = useRef(null);
  const dragFromIndex = useRef(null);
  const [draggingIndex, setDraggingIndex] = useState(null);

  const toggleReorder = (dateIndex) => {
    setReorderingDates((prev) => ({
      ...prev,
      [dateIndex]: !prev[dateIndex],
    }));
  };

  // ドラッグ開始 — ハンドルからのみ発火
  const handleDragStart = useCallback((e, dateIndex, scheduleIndex) => {
    dragDateIndex.current = dateIndex;
    dragFromIndex.current = scheduleIndex;
    setDraggingIndex(scheduleIndex);
    // ゴースト画像を小さくするため、ドラッグ対象の行要素を使う
    const row = e.currentTarget.closest("[data-schedule-row]");
    if (row) {
      e.dataTransfer.setDragImage(row, 20, 20);
    }
    e.dataTransfer.effectAllowed = "move";
  }, []);

  // ドラッグ中に他のアイテムに入った → 即座にスワップ
  const handleDragEnter = useCallback((dateIndex, scheduleIndex) => {
    if (dragDateIndex.current !== dateIndex) return;
    const fromIdx = dragFromIndex.current;
    if (fromIdx === null || fromIdx === scheduleIndex) return;

    // リアルタイムでスワップ
    setTravelData((prev) => {
      const newData = { ...prev };
      const schedules = [...newData.scheduleData[dateIndex].schedules];
      const [moved] = schedules.splice(fromIdx, 1);
      schedules.splice(scheduleIndex, 0, moved);
      schedules.forEach((s, i) => {
        s.number = i + 1;
      });
      newData.scheduleData = [...newData.scheduleData];
      newData.scheduleData[dateIndex] = {
        ...newData.scheduleData[dateIndex],
        schedules,
      };
      return newData;
    });

    dragFromIndex.current = scheduleIndex;
    setDraggingIndex(scheduleIndex);
  }, [setTravelData]);

  const handleDragEnd = useCallback(() => {
    dragDateIndex.current = null;
    dragFromIndex.current = null;
    setDraggingIndex(null);
  }, []);

  const handleInputChange = (dateIndex, scheduleIndex, field, value) => {
    const newData = { ...travelData };
    newData.scheduleData[dateIndex].schedules[scheduleIndex][field] = value;
    setTravelData(newData);
  };

  const handleAddSchedule = (dateIndex) => {
    const newData = { ...travelData };
    newData.scheduleData[dateIndex].schedules.push({
      number: newData.scheduleData[dateIndex].schedules.length + 1,
      time: "",
      title: "",
      description: "",
      budget: 0,
      links: [],
    });
    setTravelData(newData);
  };

  const handleDeleteSchedule = (dateIndex, scheduleIndex) => {
    const newData = { ...travelData };
    newData.scheduleData[dateIndex].schedules.splice(scheduleIndex, 1);
    newData.scheduleData[dateIndex].schedules.forEach((s, i) => {
      s.number = i + 1;
    });
    setTravelData(newData);
  };

  const handleLinkChange = (dateIndex, scheduleIndex, linkIndex, value) => {
    const newData = { ...travelData };
    newData.scheduleData[dateIndex].schedules[scheduleIndex].links[linkIndex] = value;
    setTravelData(newData);
  };

  const handleAddLink = (dateIndex, scheduleIndex) => {
    const newData = { ...travelData };
    newData.scheduleData[dateIndex].schedules[scheduleIndex].links.push("");
    setTravelData(newData);
  };

  const handleDeleteLink = (dateIndex, scheduleIndex, linkIndex) => {
    const newData = { ...travelData };
    newData.scheduleData[dateIndex].schedules[scheduleIndex].links.splice(linkIndex, 1);
    setTravelData(newData);
  };

  return (
    <Box
      sx={{
        width: "100%",
        padding: { xs: 2, sm: 4 },
        backgroundColor: "#f7f8fa",
        borderRadius: 3,
        overflowX: "hidden",
      }}
    >
      <Typography
        variant="h5"
        align="center"
        gutterBottom
        sx={{ color: "#333", fontWeight: "bold", marginBottom: 3 }}
      >
        日付別予定
      </Typography>

      {travelData.scheduleData.map((scheduleDay, dateIndex) => {
        const isReordering = !!reorderingDates[dateIndex];
        return (
          <Box
            key={dateIndex}
            component={Paper}
            elevation={3}
            sx={{
              padding: { xs: 2, sm: 3 },
              borderRadius: 3,
              marginBottom: 3,
              backgroundColor: "#fff",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 1,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "#444" }}>
                {scheduleDay.date}
              </Typography>
              {scheduleDay.schedules.length >= 2 && (
                <Button
                  variant={isReordering ? "contained" : "outlined"}
                  size="small"
                  startIcon={<SwapVertIcon />}
                  onClick={() => toggleReorder(dateIndex)}
                  sx={{
                    ...(isReordering
                      ? { backgroundColor: "#ff6a00", ":hover": { backgroundColor: "#e55d00" } }
                      : { color: "#ff6a00", borderColor: "#ff6a00" }),
                  }}
                >
                  {isReordering ? "完了" : "並び替え"}
                </Button>
              )}
            </Box>

            {/* スケジュール一覧 — ドラッグ領域をこの中に閉じ込める */}
            <Box>
              {scheduleDay.schedules.map((schedule, scheduleIndex) => {
                const isDragging =
                  isReordering &&
                  dragDateIndex.current === dateIndex &&
                  draggingIndex === scheduleIndex;

                return (
                  <Box
                    key={scheduleIndex}
                    data-schedule-row
                    onDragEnter={
                      isReordering
                        ? () => handleDragEnter(dateIndex, scheduleIndex)
                        : undefined
                    }
                    onDragOver={
                      isReordering ? (e) => e.preventDefault() : undefined
                    }
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      paddingY: 1.5,
                      paddingX: isReordering ? 1 : 0,
                      borderBottom: "1px solid #e0e0e0",
                      marginBottom: 1,
                      backgroundColor: isDragging
                        ? "#fff3e0"
                        : isReordering
                        ? "#fafafa"
                        : "transparent",
                      borderRadius: isReordering ? 2 : 0,
                      transition: "background-color 0.15s, box-shadow 0.15s",
                      boxShadow: isDragging
                        ? "0 2px 8px rgba(0,0,0,0.15)"
                        : "none",
                      opacity: isDragging ? 0.9 : 1,
                    }}
                  >
                    {/* Drag Handle — ここだけが draggable */}
                    {isReordering && (
                      <Box
                        draggable
                        onDragStart={(e) =>
                          handleDragStart(e, dateIndex, scheduleIndex)
                        }
                        onDragEnd={handleDragEnd}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: 36,
                          cursor: "grab",
                          color: "#999",
                          "&:hover": { color: "#333" },
                          "&:active": { cursor: "grabbing" },
                          userSelect: "none",
                        }}
                      >
                        <DragIndicatorIcon />
                      </Box>
                    )}

                    {/* Schedule Content */}
                    <Box
                      sx={{
                        flex: 1,
                        ...(isReordering
                          ? { pointerEvents: "none", opacity: 0.8 }
                          : {}),
                      }}
                    >
                      {/* Summary Row: 時間 / タイトル / 予算 */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <TimePickerInput
                          value={schedule.time}
                          onChange={(val) =>
                            handleInputChange(
                              dateIndex,
                              scheduleIndex,
                              "time",
                              val
                            )
                          }
                        />
                        <TextField
                          label="タイトル"
                          size="small"
                          value={schedule.title}
                          inputProps={{ maxLength: 14 }}
                          onChange={(e) =>
                            handleInputChange(
                              dateIndex,
                              scheduleIndex,
                              "title",
                              e.target.value
                            )
                          }
                          sx={{ flex: 1, minWidth: 100 }}
                        />
                        <TextField
                          label="予算"
                          type="number"
                          size="small"
                          value={schedule.budget}
                          onChange={(e) =>
                            handleInputChange(
                              dateIndex,
                              scheduleIndex,
                              "budget",
                              Number(e.target.value)
                            )
                          }
                          sx={{ width: 110 }}
                        />
                      </Box>

                      {/* Accordion: 詳細・リンク・削除 */}
                      {!isReordering && (
                        <Accordion
                          disableGutters
                          elevation={0}
                          sx={{
                            mt: 1,
                            "&::before": { display: "none" },
                            backgroundColor: "transparent",
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              minHeight: 32,
                              px: 0,
                              "& .MuiAccordionSummary-content": { my: 0 },
                            }}
                          >
                            <Typography sx={{ fontSize: "12px", color: "#888" }}>
                              詳細・リンク
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails
                            sx={{
                              px: 0,
                              pt: 0,
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <TextField
                              fullWidth
                              label="詳細"
                              multiline
                              rows={3}
                              value={schedule.description}
                              inputProps={{ maxLength: 1000 }}
                              onChange={(e) =>
                                handleInputChange(
                                  dateIndex,
                                  scheduleIndex,
                                  "description",
                                  e.target.value
                                )
                              }
                            />

                            {schedule.links.map((link, linkIndex) => (
                              <Box
                                key={linkIndex}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <TextField
                                  fullWidth
                                  label={`リンク${linkIndex + 1}`}
                                  value={link}
                                  onChange={(e) =>
                                    handleLinkChange(
                                      dateIndex,
                                      scheduleIndex,
                                      linkIndex,
                                      e.target.value
                                    )
                                  }
                                />
                                <IconButton
                                  onClick={() =>
                                    handleDeleteLink(
                                      dateIndex,
                                      scheduleIndex,
                                      linkIndex
                                    )
                                  }
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            ))}

                            <Button
                              startIcon={<AddIcon />}
                              onClick={() =>
                                handleAddLink(dateIndex, scheduleIndex)
                              }
                            >
                              リンク追加
                            </Button>

                            <Button
                              startIcon={<DeleteIcon />}
                              onClick={() =>
                                handleDeleteSchedule(dateIndex, scheduleIndex)
                              }
                              color="error"
                            >
                              このスケジュールを削除
                            </Button>
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Add New Schedule Button — ドラッグ領域の外 */}
            {!isReordering && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleAddSchedule(dateIndex)}
                sx={{ marginTop: 2 }}
              >
                新しいスケジュールを追加
              </Button>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default ResponsiveScheduleForm;
